import React from 'react';

export interface AmenityItem {
  id: string;
  name: string;
  description?: string;
  image: string;
  icon: string;
}

interface BrandAmenitiesProps {
  data?: {
    heading?: string;
    subheading?: string;
    items?: AmenityItem[];
  };
}

const DEFAULT_AMENITIES: AmenityItem[] = [
  {
    id: 'concierge',
    name: '24/7 Concierge & Security',
    description: 'Uninterrupted comfort and complete peace of mind — a lifestyle supported every hour of the day with refined service and exceptional care.',
    icon: 'ri-shield-check-line',
    image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=800&q=80'
  },
  {
    id: 'retail',
    name: 'Boutique Retail Experience',
    description: 'Luxury shopping just steps away curated with brands and concepts that complement an elevated lifestyle.',
    icon: 'ri-store-2-line',
    image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=800&q=80'
  },
  {
    id: 'coworking',
    name: 'Private Co-Working & Meeting Lounge',
    description: 'A sophisticated workspace where productivity meets elegance, complemented by an exclusive meeting room designed for focus, collaboration, and elevated professional gatherings.',
    icon: 'ri-building-2-line',
    image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=800&q=80'
  },
  {
    id: 'sports',
    name: 'Scenic Rooftop Multi-Purpose Sports Court',
    description: 'A versatile open-air sports court on the rooftop, enhanced by panoramic skyline views — a space where energy, performance, and breathtaking scenery come together.',
    icon: 'ri-basketball-line',
    image: 'https://images.unsplash.com/photo-1546519638-68e109498ffc?auto=format&fit=crop&w=800&q=80'
  },
  {
    id: 'pool',
    name: 'Rooftop Sky Pool with Horizon Views',
    description: 'An open-air rooftop pool that blends breathtaking skyline vistas with a serene atmosphere — creating a refreshing escape high above the city.',
    icon: 'ri-drop-line',
    image: 'https://images.unsplash.com/photo-1576013551627-0cc20b96c2a7?auto=format&fit=crop&w=800&q=80'
  },
  {
    id: 'fitness',
    name: 'Where Energy Meets Performance',
    description: 'Crafted for balance, strength, and renewal — a space where performance meets serenity in an atmosphere designed to elevate your well-being.',
    icon: 'ri-fire-line',
    image: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?auto=format&fit=crop&w=800&q=80'
  },
  {
    id: 'spa',
    name: 'Private Spa & Sauna Retreat',
    description: 'A serene sanctuary for mind, body, and soul — offering moments of deep relaxation and thoughtful rejuvenation.',
    icon: 'ri-leaf-line',
    image: 'https://images.unsplash.com/photo-1540555700478-4be289fbecef?auto=format&fit=crop&w=800&q=80'
  },
  {
    id: 'gaming',
    name: 'Entertainment & Gaming Suite',
    description: 'A premium leisure destination featuring PlayStation zones and a refined billiards area — where entertainment transforms into an elevated, memorable experience.',
    icon: 'ri-gamepad-line',
    image: 'https://images.unsplash.com/photo-1511512578047-dfb367046420?auto=format&fit=crop&w=800&q=80'
  },
  {
    id: 'dining',
    name: 'Curated Fine Dining Experience',
    description: 'Exclusive dining spaces designed for refined tastes and intimate moments, offering a setting where gastronomy and elegance meet at the highest level.',
    icon: 'ri-restaurant-2-line',
    image: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=800&q=80'
  },
  {
    id: 'kids',
    name: "Dedicated Children's Play Area",
    description: 'A beautifully crafted environment designed exclusively for children — a safe, joyful world where little ones explore, learn, and play freely.',
    icon: 'ri-bear-smile-line',
    image: 'https://images.unsplash.com/photo-1566454544259-f4b94c3d758c?auto=format&fit=crop&w=800&q=80'
  }
];

export const BrandAmenities: React.FC<BrandAmenitiesProps> = ({ data }) => {
  const rawItems = data?.items && data.items.length > 0 ? data.items : DEFAULT_AMENITIES;

  // Enrich with default descriptions if not set
  const items = rawItems.map((item, idx) => {
    const fallback = DEFAULT_AMENITIES.find(d => d.id === item.id || d.name.toLowerCase() === item.name.toLowerCase()) || DEFAULT_AMENITIES[idx];
    return {
      ...item,
      description: item.description?.trim() ? item.description : (fallback?.description || '')
    };
  });

  return (
    <section id="amenities" className="relative py-28 md:py-36 bg-[#0c0c12] text-white border-t border-white/5 overflow-hidden">
      {/* Ambient background light */}
      <div className="absolute top-1/3 -right-32 w-[500px] h-[500px] bg-[#C5A46D]/10 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-6 max-w-7xl relative z-10">
        
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#C5A46D]/10 border border-[#C5A46D]/30 text-[#C5A46D] text-xs uppercase tracking-[0.25em] font-medium">
            Signature Lifestyle
          </div>
          <h2 className="text-3xl md:text-5xl font-light tracking-wide text-white">
            Unrivalled <span className="font-semibold text-[#C5A46D]">Amenities</span>
          </h2>
          <div className="w-16 h-0.5 bg-gradient-to-r from-transparent via-[#C5A46D] to-transparent mx-auto" />
        </div>

        {/* 2-column Grid of Alternating Amenity Cards (matching the brochure layout) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-3">
          {items.map((item, idx) => {
            const row = Math.floor(idx / 2);
            const col = idx % 2;
            // Alternating checkerboard pattern:
            // Even rows (0, 2, 4): col 0 is Image-Left, col 1 is Text-Left (Image-Right)
            // Odd rows (1, 3): col 0 is Text-Left (Image-Right), col 1 is Image-Left
            const imageLeft = (row % 2 === col % 2);

            return (
              <div
                key={item.id || idx}
                className="group overflow-hidden flex flex-col sm:flex-row h-full min-h-[190px] md:min-h-[210px] shadow-lg transition-all duration-300 rounded-none sm:rounded-sm"
              >
                {imageLeft ? (
                  <>
                    {/* Image on Left */}
                    <div className="w-full sm:w-1/2 relative overflow-hidden h-48 sm:h-auto min-h-[180px] order-1">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    </div>

                    {/* Gold Text Card on Right (Text on left, Icon on right) */}
                    <div className="w-full sm:w-1/2 p-5 sm:p-6 flex items-center justify-between gap-3 bg-[#ba9866] text-[#1c1813] order-2">
                      <div className="flex-1 text-right">
                        <h3 className="text-sm sm:text-base font-bold text-[#1c1813] leading-snug">
                          {item.name}
                        </h3>
                        {item.description && (
                          <p className="mt-1.5 text-xs sm:text-[13px] text-[#2c2620] font-normal leading-relaxed">
                            {item.description}
                          </p>
                        )}
                      </div>
                      <div className="shrink-0 text-3xl sm:text-4xl text-[#1c1813] flex items-center justify-center pl-1">
                        <i className={item.icon || 'ri-star-line'}></i>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Gold Text Card on Left (Icon on left, Text on right) */}
                    <div className="w-full sm:w-1/2 p-5 sm:p-6 flex items-center justify-between gap-3 bg-[#ba9866] text-[#1c1813] order-1">
                      <div className="shrink-0 text-3xl sm:text-4xl text-[#1c1813] flex items-center justify-center pr-1">
                        <i className={item.icon || 'ri-star-line'}></i>
                      </div>
                      <div className="flex-1 text-left">
                        <h3 className="text-sm sm:text-base font-bold text-[#1c1813] leading-snug">
                          {item.name}
                        </h3>
                        {item.description && (
                          <p className="mt-1.5 text-xs sm:text-[13px] text-[#2c2620] font-normal leading-relaxed">
                            {item.description}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Image on Right */}
                    <div className="w-full sm:w-1/2 relative overflow-hidden h-48 sm:h-auto min-h-[180px] order-2">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>

      </div>
    </section>
  );
};

export default BrandAmenities;
